import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { Clickable } from '@carvana/merch-components';
import { useCallback, useContext, useRef, useState } from 'react';
import Slider from 'react-slick';
import { useDeviceType } from '~/context/common/DeviceContext';
import { RecommendedVehicle } from '~/contracts/vdp';
import { useOptimizeImage, usePreventVerticalScroll } from '~/hooks/common';
import { Arrow, Dot, Image, SliderWrapper } from './ImageCarousel.styles';
import useAnalyticsAttributes from './useAnalyticsAttributes';
import useAnchorAttributes from './useAnchorAttributes';
import useOnClick from './useOnClick';
import { TileWidth } from './VehicleTile.styles';

type OwnProps = {
  vehicle: RecommendedVehicle;
  hasLowerPrice: boolean;
};

const sliderConfig = {
  customPaging: () => (
    <Clickable data-analytics-id={`Vehicle- Tile - Nav Dots`} element={Dot} />
  ),
  dots: true,
  infinite: true,
  speed: 500,
  arrows: false,
  slidesToShow: 1,
  slidesToScroll: 1
};

export default function ImageCarousel({
  hasLowerPrice,
  vehicle: { entireDashImageUrl, imageUrl, passengerSeatImageUrl, vehicleId }
}: OwnProps) {
  const analyticsAttributes = useAnalyticsAttributes(vehicleId);
  const anchorAttributes = useAnchorAttributes(vehicleId);
  const { trackEvent } = useContext(AnalyticsContext);
  const { isDesktop } = useDeviceType();
  const [didSwipe, setDidSwipe] = useState(false);
  const images = [imageUrl, entireDashImageUrl, passengerSeatImageUrl].filter(
    Boolean
  );
  const optimizeImage = useOptimizeImage();
  const handleClick = useOnClick();
  const sliderWrapperRef = useRef(null);
  const sliderRef = useRef<Slider>(null);

  const onPrev = useCallback(() => {
    sliderRef?.current?.slickPrev();
  }, []);

  const onNext = useCallback(() => {
    sliderRef?.current?.slickNext();
  }, []);

  usePreventVerticalScroll(sliderWrapperRef);

  return (
    <SliderWrapper ref={sliderWrapperRef}>
      <Slider
        ref={sliderRef}
        {...sliderConfig}
        onSwipe={() => {
          setDidSwipe(true);
          trackEvent('Swiped - Vehicle - Tile - Carousel');
        }}
        afterChange={() => setDidSwipe(false)}
      >
        {images.map(image => (
          <a
            data-analytics-id={`Vehicles You Might Like Tile Image${
              hasLowerPrice ? ' - Lower Price' : ''
            }`}
            data-analytics-attributes={analyticsAttributes}
            data-qa="Vehicles You Might Like Tile Image"
            onClick={e => {
              if (!didSwipe) handleClick(e);
            }}
            key={`${image}`}
            {...anchorAttributes}
          >
            <Image
              alt="vehicle carousel image"
              src={optimizeImage(image, TileWidth)}
            />
          </a>
        ))}
      </Slider>
      {isDesktop && (
        <>
          <Clickable
            data-analytics-id="Vehicle Tile Arrow"
            element={Arrow}
            data-qa="desktop-arrow"
            className="arrow"
            onClick={onPrev}
          />
          <Clickable
            element={Arrow}
            data-analytics-id="Vehicle Tile Arrow"
            data-qa="desktop-arrow-reverse"
            className="arrow reversed"
            onClick={onNext}
          />
        </>
      )}
    </SliderWrapper>
  );
}
